import * as snapdom from '@zumer/snapdom';
import { tools } from '@halo/ui-chat-react';
import type { TFunction } from 'i18next';

type Source = 'group' | 'user';

export const useQrCodeHelper = (
  qrCodeCopyUrl = '',
  qrCodeName_: string,
  source: Source,
  t: TFunction
) => {
  const qrCodeName = qrCodeName_ || 'qrCode';
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const download = useCallback((url: string, fileName: string) => {
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const downloadCanvasQRCodeWithSnapdom = useCallback(
    async (cb?: (blob: Blob) => void) => {
      const node = qrCodeRef.current as HTMLElement;
      const blob = await snapdom.snapdom.toBlob(node, {
        backgroundColor: '#fff',
        type: 'png',
      });
      if (cb) {
        cb(blob);
      } else {
        const url = URL.createObjectURL(blob!);
        download(url, `${qrCodeName}.png`);
        const timer = setTimeout(() => {
          URL.revokeObjectURL(url);
          clearTimeout(timer);
        }, 1000);
      }
    },
    [download, qrCodeName]
  );

  const downloadCanvasQRCode = useCallback(
    (cb?: any) => {
      downloadCanvasQRCodeWithSnapdom((blob) => {
        if (cb) {
          cb(blob);
        } else {
          const url = URL.createObjectURL(blob!);
          download(url, `${qrCodeName}.png`);
          const timer = setTimeout(() => {
            URL.revokeObjectURL(url);
            clearTimeout(timer);
          }, 1000);
        }
      }).catch(() => {
        console.error('Failed to download canvas QR code');
      });
    },
    [download, downloadCanvasQRCodeWithSnapdom, qrCodeName]
  );

  const copyLink = useCallback(
    async (name?: string) => {
      if (source === 'group') {
        await tools.copyToClipboard(
          t('chat.groupShareLink', { groupName: name || '', shareUrl: qrCodeCopyUrl || '' })
        );
      }
      if (source === 'user') {
        await tools.copyToClipboard(
          t('chat.userShareLink', { name: name || '', shareUrl: qrCodeCopyUrl || '' })
        );
      }
    },
    [qrCodeCopyUrl, source, t]
  );

  const copyQrCode = useCallback(() => {
    downloadCanvasQRCodeWithSnapdom((blob: Blob) => {
      navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      tools.toasterCopy.success({
        title: t('tips.copied'),
      });
    });
  }, [downloadCanvasQRCodeWithSnapdom, t]);

  return {
    qrCodeRef,
    copyLink,
    copyQrCode,
    downloadCanvasQRCode,
    downloadCanvasQRCodeWithSnapdom,
  };
};
